import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Upload, Trash2, Edit2, Check, X, FileText, Loader2, Search, AlertCircle } from 'lucide-react';
import documentLibraryService from '../../../../services/documentLibraryService';
import { getFranchiseeIdForCRUD } from '../../../../App/services/appHelpers';
import ConfirmDialog from '../../../ConfirmDialog';
import useConfirm from '../../../../hooks/useConfirm';

const DocumentLibraryTab = ({ userProfile, showNotification }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const { confirmDialog, confirm } = useConfirm();

  // Upload form state
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadData, setUploadData] = useState({
    description: '',
    doc_ref: '',
    group_name: 'General',
    scope: 'shared'
  });
  const fileInputRef = useRef(null);

  const franchiseeId = getFranchiseeIdForCRUD({
    userId: userProfile?.id,
    role: userProfile?.role,
    franchiseeId: userProfile?.id
  });

  const loadDocuments = useCallback(async () => {
    if (!franchiseeId) return;
    setLoading(true);
    try {
      const result = await documentLibraryService.getDocuments(franchiseeId);
      setDocuments(result.documents || []);
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoading(false);
    }
  }, [franchiseeId]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const handleUpload = async (e) => {
    e.preventDefault();
    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      showNotification('Please select a file', 'error');
      return;
    }
    if (!uploadData.description.trim()) {
      showNotification('Description is required', 'error');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('description', uploadData.description);
      formData.append('doc_ref', uploadData.doc_ref);
      formData.append('group_name', uploadData.group_name || 'General');
      formData.append('scope', uploadData.scope);

      await documentLibraryService.uploadDocument(franchiseeId, formData);
      showNotification('Document uploaded successfully');
      setShowUploadForm(false);
      setUploadData({ description: '', doc_ref: '', group_name: 'General', scope: 'shared' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      await loadDocuments();
    } catch (error) {
      console.error('Error uploading document:', error);
      showNotification('Failed to upload document: ' + (error.message || 'Unknown error'), 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (doc) => {
    setEditingId(doc.id);
    setEditData({
      description: doc.description,
      doc_ref: doc.doc_ref || '',
      group_name: doc.group_name || '',
      is_active: doc.is_active
    });
  };

  const handleSaveEdit = async (docId) => {
    try {
      await documentLibraryService.updateDocument(franchiseeId, docId, editData);
      showNotification('Document updated');
      setEditingId(null);
      await loadDocuments();
    } catch (error) {
      showNotification('Failed to update document', 'error');
    }
  };

  const handleDelete = async (doc) => {
    const ok = await confirm({
      title: 'Delete Document',
      message: `Delete "${doc.description}"? This will also remove the file from the server.`,
      variant: 'danger',
      confirmLabel: 'Delete'
    });
    if (!ok) return;

    try {
      await documentLibraryService.deleteDocument(franchiseeId, doc.id);
      showNotification('Document deleted');
      await loadDocuments();
    } catch (error) {
      showNotification('Failed to delete document', 'error');
    }
  };

  const filteredDocs = documents.filter(doc => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (doc.description || '').toLowerCase().includes(q) ||
           (doc.doc_ref || '').toLowerCase().includes(q) ||
           (doc.group_name || '').toLowerCase().includes(q) ||
           (doc.filename || '').toLowerCase().includes(q);
  });

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Document Library</h2>
          <p className="text-gray-600 text-sm mt-1">Manage shared documents available for email attachments</p>
        </div>
        <button
          onClick={() => setShowUploadForm(!showUploadForm)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
        >
          <Upload size={16} />
          Upload
        </button>
      </div>

      {/* Upload Form */}
      {showUploadForm && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="font-medium text-gray-900 mb-3">Upload New Document</h3>
          <form onSubmit={handleUpload} className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">File *</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  className="w-full text-sm border border-gray-300 rounded-lg file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-blue-100 file:text-blue-700 file:font-medium file:cursor-pointer"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <input
                  type="text"
                  value={uploadData.description}
                  onChange={(e) => setUploadData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  placeholder="Document description"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reference Code</label>
                <input
                  type="text"
                  value={uploadData.doc_ref}
                  onChange={(e) => setUploadData(prev => ({ ...prev, doc_ref: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. COSHH-001"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Group</label>
                <input
                  type="text"
                  value={uploadData.group_name}
                  onChange={(e) => setUploadData(prev => ({ ...prev, group_name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g. COSHH, Insurance, Certificates"
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={uploading}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
              >
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                {uploading ? 'Uploading...' : 'Upload Document'}
              </button>
              <button
                type="button"
                onClick={() => setShowUploadForm(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 text-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Search */}
      <div className="relative mb-4 max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search documents..."
          className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Documents Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={24} className="animate-spin text-blue-500 mr-2" />
          <span className="text-gray-500">Loading documents...</span>
        </div>
      ) : filteredDocs.length === 0 ? (
        <div className="text-center py-12">
          <FileText size={48} className="mx-auto text-gray-300 mb-4" />
          <p className="text-gray-500">
            {searchQuery ? 'No documents match your search' : 'No documents uploaded yet'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 uppercase">Ref</th>
                <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 uppercase">Group</th>
                <th className="text-left px-3 py-2 text-xs font-medium text-gray-500 uppercase">File</th>
                <th className="text-right px-3 py-2 text-xs font-medium text-gray-500 uppercase">Size</th>
                <th className="text-center px-3 py-2 text-xs font-medium text-gray-500 uppercase">Active</th>
                <th className="w-20"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredDocs.map(doc => (
                <tr key={doc.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 text-gray-500 font-mono text-xs">
                    {editingId === doc.id ? (
                      <input
                        type="text"
                        value={editData.doc_ref}
                        onChange={(e) => setEditData(prev => ({ ...prev, doc_ref: e.target.value }))}
                        className="w-20 px-1 py-0.5 border rounded text-xs"
                      />
                    ) : (
                      doc.doc_ref || '-'
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-800">
                    {editingId === doc.id ? (
                      <input
                        type="text"
                        value={editData.description}
                        onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
                        className="w-full px-1 py-0.5 border rounded text-sm"
                      />
                    ) : (
                      doc.description
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-600">
                    {editingId === doc.id ? (
                      <input
                        type="text"
                        value={editData.group_name}
                        onChange={(e) => setEditData(prev => ({ ...prev, group_name: e.target.value }))}
                        className="w-24 px-1 py-0.5 border rounded text-sm"
                      />
                    ) : (
                      doc.group_name || '-'
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-500 text-xs truncate max-w-[150px]" title={doc.filename}>
                    {doc.filename}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-400 text-xs whitespace-nowrap">
                    {formatFileSize(doc.file_size)}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {editingId === doc.id ? (
                      <input
                        type="checkbox"
                        checked={editData.is_active}
                        onChange={(e) => setEditData(prev => ({ ...prev, is_active: e.target.checked }))}
                        className="rounded border-gray-300 text-blue-600"
                      />
                    ) : (
                      <span className={`inline-block w-2 h-2 rounded-full ${doc.is_active ? 'bg-green-500' : 'bg-gray-300'}`} />
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1 justify-end">
                      {editingId === doc.id ? (
                        <>
                          <button onClick={() => handleSaveEdit(doc.id)} className="p-1 text-green-600 hover:text-green-700" title="Save">
                            <Check size={14} />
                          </button>
                          <button onClick={() => setEditingId(null)} className="p-1 text-gray-400 hover:text-gray-600" title="Cancel">
                            <X size={14} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => handleEdit(doc)} className="p-1 text-gray-400 hover:text-blue-600" title="Edit">
                            <Edit2 size={14} />
                          </button>
                          <button onClick={() => handleDelete(doc)} className="p-1 text-gray-400 hover:text-red-600" title="Delete">
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog {...confirmDialog} />
    </div>
  );
};

export default DocumentLibraryTab;
